import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../lib/store";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Field, TextInput } from "../ui/Field";
import { appPath } from "./routes";

/**
 * Busca global do topo, no formato do protótipo: um campo, os resultados logo
 * abaixo e o Fechar no rodapé.
 *
 * Procura leads, tarefas e funis no estado já carregado — a mesma fonte das
 * listas, então o resultado nunca diverge do que as telas mostram. Cada
 * resultado navega de verdade: o lead abre o perfil, a tarefa abre a lista de
 * Tarefas filtrada por ela e o funil abre o editor.
 */
const MAX_RESULTS = 12;

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

interface SearchHit {
  key: string;
  title: string;
  sub: string;
  to: string;
}

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { leads, tasks, funnels } = useStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
  }, [open]);

  const results = useMemo<SearchHit[]>(() => {
    const term = normalize(query.trim());
    if (!term) return [];
    const leadById = new Map(leads.map((lead) => [lead.id, lead]));
    const hits: SearchHit[] = [
      ...leads
        .filter((lead) => !lead.archived)
        .filter((lead) =>
          [lead.name, lead.company, lead.email ?? "", lead.phone ?? "", lead.instagram ?? ""].some((field) =>
            normalize(field).includes(term),
          ),
        )
        .map((lead) => ({
          key: `lead:${lead.id}`,
          title: lead.name,
          sub: lead.company,
          to: appPath(`leads/${lead.id}`),
        })),
      ...tasks
        .filter((task) => normalize(task.description).includes(term))
        .map((task) => {
          const owner = task.leadId ? leadById.get(task.leadId) : undefined;
          return {
            key: `task:${task.id}`,
            title: task.description,
            sub: `Tarefa · ${formatDay(task.dueAt)}${owner ? ` · ${owner.name}` : " · Interna"}`,
            to: `${appPath("tarefas")}?modo=lista&q=${encodeURIComponent(task.description)}`,
          };
        }),
      ...funnels
        .filter((funnel) => !funnel.archived && normalize(funnel.name).includes(term))
        .map((funnel) => ({
          key: `funnel:${funnel.id}`,
          title: funnel.name,
          sub: "Funil",
          to: appPath(`funis/${funnel.id}`),
        })),
    ];
    return hits.slice(0, MAX_RESULTS);
  }, [leads, tasks, funnels, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const go = (hit: SearchHit) => {
    onClose();
    navigate(hit.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = results[activeIndex];
      if (hit) go(hit);
    }
  };

  const term = query.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buscar no CosmosLeads"
      initialFocusRef={inputRef}
      className="cx-search-dialog"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <Field label="Lead, empresa, tarefa ou fluxo">
        {({ id }) => (
          <TextInput
            id={id}
            ref={inputRef}
            type="search"
            autoComplete="off"
            placeholder="Digite para buscar…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
          />
        )}
      </Field>

      {results.length === 0 ? (
        <div className="cx-search-empty">
          <strong>{term ? "Nenhum resultado" : "Encontre seu próximo passo"}</strong>
          <p>{term ? "Tente outro termo." : "Busque pelo nome do lead, empresa, tarefa ou fluxo."}</p>
        </div>
      ) : (
        <ul className="cx-search-results">
          {results.map((hit, index) => (
            <li key={hit.key}>
              <button
                type="button"
                className="cx-search-result"
                data-active={index === activeIndex ? "true" : undefined}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => go(hit)}
              >
                <b>{hit.title}</b>
                <span>{hit.sub}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "sem data";
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo" });
}
